import { useCallback, useEffect, useRef, useState } from 'react';

import { buildErrorAnnouncement } from '../utils/errorAnnouncement';

/**
 * ACP-2092 / ACP-2077 (Section 508): a rejected submit has to be perceivable without
 * sight. Rendering an error message is not enough — a screen reader only reads what it
 * is told to read, so a user who presses Save and stays put has no way to know the
 * press did anything until they happen to navigate back onto the field.
 *
 * Two things fix that, and both are needed:
 *
 * - An assertive live region naming every field that needs attention, so the errors
 *   are reported as they appear.
 * - Focus moved to the first invalid control, so a keyboard or screen reader user
 *   lands where the correction goes instead of on a control that appeared inert.
 *
 * This was first solved for the application workflow's Next button. The same defect
 * was then found by Section 508 re-testing on the Roster Owners tab, the Add User to
 * Roster modal, and the nested FormList — every other place in the product that
 * validates on submit. Keeping one implementation is the point: a second copy is how
 * the next form ships with the same finding.
 */

/**
 * One entry per invalid field, in document order.
 *
 * A radio group marks every one of its options invalid, because a <fieldset> exposes
 * neither aria-invalid nor aria-required in the accessibility tree. Left as-is that
 * counted one question twice and read its options back as if they were fields —
 * "6 fields need your attention: ..., Yes required, No required". Collapse each group
 * to its first radio, and let the group's own name stand in for it.
 */
function collapseRadioGroups(invalidControls: HTMLElement[]): HTMLElement[] {
  const seenGroups = new Set<string>();

  return invalidControls.filter((element) => {
    if (!(element instanceof HTMLInputElement) || element.type !== 'radio') return true;

    const group = element.name;
    if (!group || seenGroups.has(group)) return false;
    seenGroups.add(group);

    return true;
  });
}

/** Move focus to the first invalid control, in document order. */
function focusFirstInvalid(invalidControls: HTMLElement[]): void {
  // A radio group marks its `fieldset` invalid, which cannot take focus, so walk the
  // invalid elements in document order and focus the first control that can — falling
  // back to the first focusable descendant of a container.
  for (const element of invalidControls) {
    const target = element.matches('input, select, textarea, button, [tabindex]')
      ? element
      : element.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');

    if (target) {
      target.focus();

      return;
    }
  }
}

export interface UseValidationAnnouncementOptions {
  /**
   * Which fields are currently reporting an error, as a value effects can compare —
   * typically the error keys joined together. Once it goes empty the announcement is
   * cleared, because a screen reader re-reading a standing region would otherwise
   * report errors that no longer exist.
   */
  errorSignature?: string;
}

export interface UseValidationAnnouncement<T extends HTMLElement> {
  /** Text for the live region. Render it through `ValidationAnnouncement`. */
  announcement: string;
  /**
   * Call after setting the error state on a rejected submit. Reads the controls that
   * the render marked `aria-invalid`, announces them, and moves focus to the first.
   *
   * Safe to call in the same handler as the `setState` that produces the errors:
   * React commits both before effects run, so the DOM is already up to date.
   */
  reportInvalidFields: () => void;
  /**
   * Attach to the element containing the validated fields. Scoping matters — an
   * unscoped lookup would sweep in invalid controls from elsewhere on the page, so a
   * modal would announce fields behind it. Leave it unattached to search the document.
   */
  scopeRef: React.RefObject<T | null>;
}

export function useValidationAnnouncement<T extends HTMLElement = HTMLDivElement>({
  errorSignature,
}: UseValidationAnnouncementOptions = {}): UseValidationAnnouncement<T> {
  const scopeRef = useRef<T>(null);
  const [announcement, setAnnouncement] = useState('');

  // Gated on the attempt, not on the errors: the error state also changes while typing
  // (editing a field clears its own error), and reacting to that would yank focus out
  // from under the user mid-edit. Counting attempts rather than latching a flag also
  // means a second press on an unchanged form still moves focus.
  const [attempt, setAttempt] = useState(0);

  const reportInvalidFields = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  useEffect(() => {
    if (attempt === 0) return;

    const root: ParentNode = scopeRef.current ?? document;
    const invalid = collapseRadioGroups(Array.from(root.querySelectorAll<HTMLElement>('[aria-invalid="true"]')));

    setAnnouncement(buildErrorAnnouncement(invalid));
    focusFirstInvalid(invalid);
  }, [attempt]);

  useEffect(() => {
    if (errorSignature === undefined) return;

    if (!errorSignature) {
      setAnnouncement('');
    }
  }, [errorSignature]);

  return { announcement, reportInvalidFields, scopeRef };
}
